/**
 * AI Calling permission constants and helpers.
 *
 * Backend should issue these abilities in userData.abilities on login.
 * Special per-user grants from admin/config/ai-calling are OR'd with CASL.
 */

/**
 * Kill-switch for Jobs "AI Calling" button and Admin Settings →
 * Agents / AI Calling tabs. Set to `false` to hide those surfaces.
 * Does NOT affect Admin Settings → Master data → DID Assignment
 * (that uses canManageMasterData, not these helpers).
 */
export const AI_CALLING_UI_ENABLED = true

export const AI_CALLING_PERMISSIONS = {
  MODULE: 'ai_calling',
  PROMPT: 'ai_calling_prompt',
}

export const AI_CALLING_ACTIONS = {
  READ: 'read',
  UPDATE: 'update',
  CREATE: 'create',
  MANAGE: 'manage',
}

/**
 * Backend reference: suggested abilities by role type.
 */
export const ROLE_ABILITIES_MAP = {
  viewer: [
    { action: 'read', subject: AI_CALLING_PERMISSIONS.MODULE },
  ],
  scriptOwner: [
    { action: 'read', subject: AI_CALLING_PERMISSIONS.MODULE },
    { action: 'update', subject: AI_CALLING_PERMISSIONS.PROMPT },
  ],
  runner: [
    { action: 'read', subject: AI_CALLING_PERMISSIONS.MODULE },
    { action: 'create', subject: AI_CALLING_PERMISSIONS.MODULE },
  ],
  recruiter: [
    { action: 'read', subject: AI_CALLING_PERMISSIONS.MODULE },
    { action: 'update', subject: AI_CALLING_PERMISSIONS.PROMPT },
    { action: 'create', subject: AI_CALLING_PERMISSIONS.MODULE },
  ],
  admin: [
    { action: 'manage', subject: AI_CALLING_PERMISSIONS.MODULE },
  ],
}

function hasPermission(canFn, action, subject) {
  if (typeof canFn !== 'function') return false
  return !!canFn(action, subject)
}

function getCurrentEmpId() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}')
    return userData?.emp_id || null
  } catch (e) {
    return null
  }
}

/**
 * Find special-user grant row for the current (or given) emp_id.
 * @param {Array<{ emp_id: string, view?: boolean, prompt?: boolean, run?: boolean }>} specialUsers
 * @param {string} [empId]
 */
export function getSpecialUserGrant(specialUsers, empId) {
  const id = empId || getCurrentEmpId()
  if (!id || !Array.isArray(specialUsers)) return null
  return specialUsers.find(u => u && String(u.emp_id) === String(id)) || null
}

/** Org admin — same as invoice / Admin Settings (`roleId === 1`). */
function isAdminRole() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}') || {}
    const roleId = userData.roleId ?? userData.roleID ?? userData.role_id
    return Number(roleId) === 1
  } catch (e) {
    return false
  }
}

export function canManageAiCalling(canFn) {
  if (!AI_CALLING_UI_ENABLED) return false
  return (
    isAdminRole()
    || hasPermission(canFn, AI_CALLING_ACTIONS.MANAGE, AI_CALLING_PERMISSIONS.MODULE)
    || hasPermission(canFn, 'manage', 'all')
  )
}

export function canEditAiPrompt(canFn, specialUsers = null) {
  if (!AI_CALLING_UI_ENABLED) return false
  if (canManageAiCalling(canFn)) return true
  if (hasPermission(canFn, AI_CALLING_ACTIONS.UPDATE, AI_CALLING_PERMISSIONS.PROMPT)) return true
  const grant = getSpecialUserGrant(specialUsers)
  return !!(grant && grant.prompt)
}

export function canRunAiCalling(canFn, specialUsers = null) {
  if (!AI_CALLING_UI_ENABLED) return false
  if (canManageAiCalling(canFn)) return true
  if (hasPermission(canFn, AI_CALLING_ACTIONS.CREATE, AI_CALLING_PERMISSIONS.MODULE)) return true
  const grant = getSpecialUserGrant(specialUsers)
  return !!(grant && grant.run)
}

export function canViewAiCalling(canFn, specialUsers = null) {
  if (!AI_CALLING_UI_ENABLED) return false
  if (canManageAiCalling(canFn)) return true
  if (hasPermission(canFn, AI_CALLING_ACTIONS.READ, AI_CALLING_PERMISSIONS.MODULE)) return true
  if (canEditAiPrompt(canFn, specialUsers)) return true
  if (canRunAiCalling(canFn, specialUsers)) return true
  const grant = getSpecialUserGrant(specialUsers)
  return !!(grant && (grant.view || grant.prompt || grant.run))
}

export default {
  AI_CALLING_UI_ENABLED,
  AI_CALLING_PERMISSIONS,
  AI_CALLING_ACTIONS,
  ROLE_ABILITIES_MAP,
  getSpecialUserGrant,
  canViewAiCalling,
  canEditAiPrompt,
  canRunAiCalling,
  canManageAiCalling,
}
